import { useCallback, useEffect, useRef, useState } from "react";

import { GUEST_SAVE_FOR_LATER_KEY } from "../schemas/save-for-later";
import { guestSaveForLaterUtils } from "../utils/guest-save-for-later-utils";
import {
  SFL_COUNT_COOKIE,
  writePersistedCartCount,
} from "./persisted-cart-count";

export type UseGuestSaveForLater = {
  productIds: string[];
  isInList: (productId: string) => boolean;
  /** `false` when the localStorage write failed — caller must not claim success. */
  add: (productId: string) => boolean;
  /** `false` when the localStorage write failed — caller must not claim success. */
  remove: (productId: string) => boolean;
  clear: () => void;
};

/**
 * Reactive view of `localStorage[GUEST_SAVE_FOR_LATER_KEY]` plus mutators
 * that update local state directly. Cross-tab writes from other browser
 * tabs come in via the `storage` event; same-tab mutations call the
 * returned mutators, which both write localStorage and `setState` so the
 * hook owns the source of truth without a custom event channel.
 */
export const useGuestSaveForLater = (): UseGuestSaveForLater => {
  const [productIds, setProductIds] = useState<string[]>(() =>
    guestSaveForLaterUtils.getList().items.map(i => i.productId),
  );

  // COM-63 — the mutators compute `next` synchronously (to return the
  // write-success flag), so NOT from the render closure: a second mutation in
  // the same React tick would build off the pre-first-call snapshot and drop
  // an item. This ref is synced post-commit AND inside the mutators, so they
  // chain.
  const productIdsRef = useRef(productIds);
  useEffect(() => {
    productIdsRef.current = productIds;
  }, [productIds]);

  // COM-294: mirror the list length into a cookie so the cart loader can size the
  // guest's Save for Later skeleton (covers mount, add/remove/clear, other tabs).
  useEffect(() => {
    writePersistedCartCount(productIds.length, SFL_COUNT_COOKIE);
  }, [productIds]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const refresh = () => {
      const next = guestSaveForLaterUtils.getList().items.map(i => i.productId);
      // Keep the ref authoritative synchronously (not just via the post-commit
      // sync effect): a cross-tab write can land immediately before a same-tick
      // `add`/`remove` in this tab, and those mutators read `productIdsRef` to
      // build `next` — a stale ref would drop the other tab's item on write.
      productIdsRef.current = next;
      setProductIds(prev =>
        prev.length === next.length && prev.every((v, i) => v === next[i])
          ? prev
          : next,
      );
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === GUEST_SAVE_FOR_LATER_KEY || e.key === null) refresh();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // COM-63 (P2-9b) — write + read the success flag synchronously, NOT inside
  // the `setProductIds` updater (React may defer/replay it, so the flag would
  // read back a stale `true` and the basket caller would drop the cart line
  // believing the save succeeded). Commit ref + state only when the write
  // persisted; return `false` so the caller can surface it. A no-op (already
  // in / not in the list) counts as success.
  const add = useCallback((productId: string): boolean => {
    const current = productIdsRef.current;
    if (current.includes(productId)) return true;
    const next = [...current, productId];
    const persisted = guestSaveForLaterUtils.write({
      items: next.map(id => ({ productId: id })),
    });
    if (persisted) {
      productIdsRef.current = next;
      setProductIds(next);
    }
    return persisted;
  }, []);

  const remove = useCallback((productId: string): boolean => {
    const current = productIdsRef.current;
    if (!current.includes(productId)) return true;
    const next = current.filter(id => id !== productId);
    const persisted = guestSaveForLaterUtils.write({
      items: next.map(id => ({ productId: id })),
    });
    if (persisted) {
      productIdsRef.current = next;
      setProductIds(next);
    }
    return persisted;
  }, []);

  const clear = useCallback(() => {
    if (productIdsRef.current.length === 0) return;
    guestSaveForLaterUtils.clear();
    productIdsRef.current = [];
    setProductIds([]);
  }, []);

  const isInList = useCallback(
    (productId: string): boolean => productIds.includes(productId),
    [productIds],
  );

  return { productIds, isInList, add, remove, clear };
};
